import type { RefObject } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Menu, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IamuWordmark } from "@/components/brand/logo";
import { useOptionalProject } from "@/components/project-context";
import { ThemeToggle } from "./theme-toggle";
import { NotificationBell } from "@/components/notifications/notification-bell";
import { useUnreadTabBadge } from "@/hooks/use-unread-tab-badge";
import { SyncQueueWidget } from "./sync-queue-widget";
import { cn } from "@/lib/utils";
import { useBreadcrumbCustomLabel } from "./breadcrumb-context";

interface Crumb {
  label: string;
  to?: string;
}

const SECTION_LABELS: Record<string, string> = {
  scenarios: "Scenarios",
  walkthroughs: "Walkthroughs",
  findings: "Findings",
  personas: "Personas",
  sources: "Sources",
  settings: "Settings",
  builds: "Builds",
  context: "Context",
  understanding: "Understanding",
  terminology: "Terminology",
  exports: "Exports",
  "live-walkthrough": "Live Walkthrough",
  bootstrap: "Bootstrap",
};

function useBreadcrumbs(): Crumb[] {
  const location = useLocation();
  const project = useOptionalProject();
  const customLabel = useBreadcrumbCustomLabel();
  const pathname = location.pathname;

  const crumbs: Crumb[] = [];

  crumbs.push({ label: "Projects", to: "/projects" });

  if (project && pathname.startsWith(`/projects/${project.id}`)) {
    crumbs.push({
      label: project.name,
      to: `/projects/${project.id}`,
    });

    const subPath = pathname.slice(`/projects/${project.id}`.length);
    if (!subPath || subPath === "/") {
      crumbs.push({ label: "Overview" });
      return crumbs;
    }

    const segments = subPath.split("/").filter(Boolean);
    const section = segments[0];

    if (section && SECTION_LABELS[section]) {
      const sectionLabel = SECTION_LABELS[section];
      const hasMore = segments.length > 1;
      const isHotspots =
        section === "findings" && location.search.includes("tab=hotspots");

      crumbs.push({
        label: sectionLabel,
        to:
          hasMore || isHotspots
            ? `/projects/${project.id}/${section}`
            : undefined,
      });

      if (isHotspots && !hasMore) {
        crumbs.push({
          label: "Hotspots",
        });
      }

      if (hasMore) {
        if (section === "walkthroughs" && segments[1] === "builds") {
          const hasBuildDetail = segments.length > 2;
          crumbs.push({
            label: "Builds",
            to: hasBuildDetail
              ? `/projects/${project.id}/walkthroughs/builds`
              : undefined,
          });
          if (hasBuildDetail) {
            crumbs.push({ label: customLabel || "Build" });
          }
        } else if (segments[1] === "edit") {
          crumbs.push({ label: "Edit" });
        } else if (segments[1] === "new") {
          crumbs.push({ label: "New" });
        } else if (segments[1] === "discover") {
          crumbs.push({ label: "Discover" });
        } else {
          const hasChild = segments.length > 2;
          crumbs.push({
            label: customLabel || "Detail",
            to: hasChild
              ? `/projects/${project.id}/${section}/${segments[1]}`
              : undefined,
          });
          if (segments[2]) {
            crumbs.push({
              label:
                segments[2] === "edit"
                  ? "Edit"
                  : segments[2].charAt(0).toUpperCase() + segments[2].slice(1),
            });
          }
        }
      }
    } else if (section) {
      crumbs.push({
        label: section.charAt(0).toUpperCase() + section.slice(1),
      });
    }
  } else {
    if (pathname.startsWith("/settings")) {
      crumbs.push({ label: "Settings" });
    } else if (pathname.startsWith("/account")) {
      crumbs.push({ label: "Account" });
    } else if (pathname.startsWith("/sources")) {
      crumbs.push({ label: "Sources" });
    } else if (pathname.startsWith("/search")) {
      crumbs.push({ label: "Search" });
    }
  }

  return crumbs;
}

export function TopBar({
  onMenuToggle,
  menuTriggerRef,
}: {
  onMenuToggle?: () => void;
  menuTriggerRef?: RefObject<HTMLButtonElement | null>;
}) {
  const crumbs = useBreadcrumbs();
  const project = useOptionalProject();
  useUnreadTabBadge();

  const handleOpenSearch = () => {
    document.dispatchEvent(
      new KeyboardEvent("keydown", {
        key: "k",
        metaKey: true,
        bubbles: true,
      }),
    );
  };

  return (
    <>
      {/* Mobile top bar */}
      <header
        className="flex h-12 shrink-0 items-center justify-between border-b border-sidebar-border bg-sidebar px-4 md:hidden"
        role="banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          {onMenuToggle && (
            <Button
              ref={menuTriggerRef}
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-sidebar-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
              onClick={onMenuToggle}
              aria-label="Open navigation menu"
            >
              <Menu className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}

          <Link
            to="/projects"
            className="flex items-center shrink-0"
            aria-label="Home"
          >
            <IamuWordmark className="text-sidebar-foreground" />
          </Link>

          {project && (
            <div className="flex items-center gap-1.5 min-w-0 border-l border-sidebar-border/80 pl-2">
              <span className="text-xs font-medium text-sidebar-foreground truncate max-w-[130px]">
                {project.name}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <SyncQueueWidget isMobile />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-sidebar-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
            onClick={handleOpenSearch}
            aria-label="Open search and command palette"
          >
            <Search className="h-4 w-4" aria-hidden="true" />
          </Button>
          <NotificationBell />
          <ThemeToggle className="text-sidebar-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground" />
        </div>
      </header>

      {/* Desktop workbench top bar */}
      <header
        className="hidden h-12 shrink-0 items-center justify-between border-b border-border/70 bg-background/95 px-6 backdrop-blur-sm md:flex z-20 gap-4"
        role="banner"
      >
        {/* Breadcrumb navigation */}
        <nav
          aria-label="Breadcrumb navigation"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-xs overflow-hidden"
        >
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            return (
              <div
                key={`${crumb.label}-${index}`}
                className="flex items-center gap-1.5 min-w-0 shrink"
              >
                {index > 0 && (
                  <ChevronRight
                    className="h-3 w-3 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                )}
                {crumb.to && !isLast ? (
                  <Link
                    to={crumb.to}
                    className="truncate max-w-[140px] lg:max-w-[200px] font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    className={cn(
                      "truncate font-semibold",
                      isLast
                        ? "text-foreground max-w-[220px] lg:max-w-[340px]"
                        : "text-muted-foreground max-w-[140px] lg:max-w-[200px]",
                    )}
                    aria-current={isLast ? "page" : undefined}
                  >
                    {crumb.label}
                  </span>
                )}
              </div>
            );
          })}
        </nav>

        {/* Global tools & actions */}
        <div className="flex items-center gap-2.5 shrink-0">
          <SyncQueueWidget />
          <button
            type="button"
            onClick={handleOpenSearch}
            className="flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
            aria-label="Open command palette"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden lg:inline">Search or jump to...</span>
            <span className="lg:hidden">Search...</span>
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-xs font-mono font-medium">
              ⌘K
            </kbd>
          </button>

          <NotificationBell />
          <ThemeToggle />
        </div>
      </header>
    </>
  );
}
